// src/components/pages/templates/TemplateManagerPage.tsx

import React, { useState, useContext, useEffect } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  TextInput,
  Alert,
} from "react-native";
import SessionContext from "../../../../contexts/session";
import {
  getSoapTemplate,
  createOrUpdateSoapTemplate,
  GetTemplateResponseSchema,
  AdjustSoapTemplateRequestSchema,
} from "../../../../services/api-documentation";

const TemplateManagerPage = () => {
  const [template, setTemplate] = useState<GetTemplateResponseSchema | null>(
    null,
  );
  const [editMode, setEditMode] = useState(false);
  const [templateData, setTemplateData] = useState<string>("");
  const [integrationType, setIntegrationType] = useState<string>("");
  const [loading, setLoading] = useState(false);

  const sessionContext = useContext(SessionContext);
  const session = sessionContext?.session;

  const loadTemplate = async () => {
    if (!session) return;

    setLoading(true);
    try {
      const templateResponse = await getSoapTemplate(session);
      setTemplate(templateResponse);
      setTemplateData(JSON.stringify(templateResponse.template_data, null, 2));
    } catch (error) {
      console.error("Failed to load template:", error);
      Alert.alert("Error", "Failed to load template");
    } finally {
      setLoading(false);
    }
  };

  const saveTemplate = async () => {
    if (!session) return;

    setLoading(true);
    try {
      let parsedData;
      try {
        parsedData = JSON.parse(templateData);
      } catch (e) {
        Alert.alert("Error", "Invalid JSON format");
        return;
      }

      const request: AdjustSoapTemplateRequestSchema = {
        template_data: parsedData,
        integration_type: integrationType || undefined,
      };

      const response = await createOrUpdateSoapTemplate(session, request);
      if (response.template_data && response.template_source) {
        setTemplate({
          template_data: response.template_data,
          template_source: response.template_source,
        });
      }
      setEditMode(false);
      Alert.alert("Success", "Template updated successfully");
    } catch (error) {
      console.error("Failed to save template:", error);
      Alert.alert("Error", "Failed to save template");
    } finally {
      setLoading(false);
    }
  };

  const cancelEdit = () => {
    if (template) {
      setTemplateData(JSON.stringify(template.template_data, null, 2));
    }
    setEditMode(false);
  };

  useEffect(() => {
    loadTemplate();
  }, [session]);

  if (!session) {
    return (
      <View style={styles.container}>
        <Text style={styles.errorText}>Please log in to manage templates</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container}>
      <Text style={styles.title}>📝 SOAP Templates</Text>
      <Text style={styles.subtitle}>
        Manage your clinic's SOAP note templates
      </Text>

      {/* Template Info Section */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Current Template</Text>
          <TouchableOpacity
            style={styles.refreshButton}
            onPress={loadTemplate}
            disabled={loading}
          >
            <Text style={styles.refreshButtonText}>
              {loading ? "Loading..." : "🔄 Refresh"}
            </Text>
          </TouchableOpacity>
        </View>

        {template && (
          <View style={styles.templateInfo}>
            <Text style={styles.infoText}>
              <Text style={styles.infoLabel}>Source:</Text>{" "}
              {template.template_source}
            </Text>
          </View>
        )}
      </View>

      {/* Template Editor Section */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Template Editor</Text>
          {!editMode ? (
            <TouchableOpacity
              style={styles.editButton}
              onPress={() => setEditMode(true)}
              disabled={!template}
            >
              <Text style={styles.editButtonText}>✏️ Edit</Text>
            </TouchableOpacity>
          ) : (
            <View style={styles.editActions}>
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={cancelEdit}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.saveButton}
                onPress={saveTemplate}
                disabled={loading}
              >
                <Text style={styles.saveButtonText}>
                  {loading ? "Saving..." : "💾 Save"}
                </Text>
              </TouchableOpacity>
            </View>
          )}
        </View>

        {editMode && (
          <View style={styles.editForm}>
            <Text style={styles.inputLabel}>Integration Type (optional):</Text>
            <TextInput
              style={styles.textInput}
              value={integrationType}
              onChangeText={setIntegrationType}
              placeholder="e.g., drchrono, custom"
            />
          </View>
        )}

        <View style={styles.templateEditor}>
          <Text style={styles.inputLabel}>Template Data (JSON):</Text>
          <TextInput
            style={[styles.textArea, !editMode && styles.readOnly]}
            value={templateData}
            onChangeText={setTemplateData}
            multiline
            numberOfLines={20}
            editable={editMode}
            placeholder="Template data will appear here..."
          />
        </View>
      </View>

      {/* Debug info - remove this in production */}
      {process.env.EXPO_PUBLIC_ENV === "local" && template && (
        <View style={styles.debugSection}>
          <Text style={styles.debugText}>
            Debug: Template Source: {template.template_source}
          </Text>
          <Text style={styles.debugText}>
            Template Keys: {Object.keys(template.template_data).join(", ")}
          </Text>
        </View>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    backgroundColor: "#f8f9fa",
  },
  title: {
    fontSize: 28,
    fontWeight: "bold",
    marginBottom: 8,
    color: "#212529",
  },
  subtitle: {
    fontSize: 16,
    color: "#6c757d",
    marginBottom: 24,
  },
  errorText: {
    textAlign: "center",
    color: "#dc3545",
    fontSize: 16,
    marginTop: 50,
  },
  section: {
    marginBottom: 24,
  },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: "600",
    color: "#495057",
  },
  refreshButton: {
    backgroundColor: "#17a2b8",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
  },
  refreshButtonText: {
    color: "white",
    fontSize: 14,
    fontWeight: "600",
  },
  editButton: {
    backgroundColor: "#ffc107",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
  },
  editButtonText: {
    color: "white",
    fontSize: 14,
    fontWeight: "600",
  },
  editActions: {
    flexDirection: "row",
    gap: 8,
  },
  cancelButton: {
    backgroundColor: "#6c757d",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
  },
  cancelButtonText: {
    color: "white",
    fontSize: 14,
    fontWeight: "600",
  },
  saveButton: {
    backgroundColor: "#28a745",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
  },
  saveButtonText: {
    color: "white",
    fontSize: 14,
    fontWeight: "600",
  },
  templateInfo: {
    backgroundColor: "white",
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#dee2e6",
  },
  infoText: {
    fontSize: 14,
    color: "#495057",
  },
  infoLabel: {
    fontWeight: "600",
  },
  editForm: {
    marginBottom: 16,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#495057",
    marginBottom: 8,
  },
  textInput: {
    backgroundColor: "white",
    borderWidth: 1,
    borderColor: "#ced4da",
    borderRadius: 6,
    padding: 12,
    fontSize: 14,
  },
  templateEditor: {
    backgroundColor: "white",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#dee2e6",
    padding: 16,
  },
  textArea: {
    backgroundColor: "white",
    borderWidth: 1,
    borderColor: "#ced4da",
    borderRadius: 6,
    padding: 12,
    fontSize: 12,
    fontFamily: "monospace",
    minHeight: 400,
    textAlignVertical: "top",
  },
  readOnly: {
    backgroundColor: "#f8f9fa",
    color: "#6c757d",
  },
  debugSection: {
    marginTop: 20,
    padding: 10,
    backgroundColor: "#f8f9fa",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#dee2e6",
  },
  debugText: {
    fontSize: 12,
    color: "#6c757d",
    marginBottom: 4,
  },
});

export default TemplateManagerPage;
